// Copyright (c) 2021 WiseTime. All rights reserved.

import React, { useRef } from "react"
import ReactDOM from "react-dom"
import styled, { css } from "styled-components/macro"
import { useClickAway, useKey } from "react-use"
import { Loader } from "./styles"
import { DIALOG_WRAPPER_CLASSNAME } from "features/shared/classNames"
import { darken } from "polished"

const DialogWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 107;
  background-color: rgba(0, 0, 0, .6);
`

export const Modal = styled.div`
  position: relative;
  width: calc(100% - 40px);
  max-width: 38rem;
  background-color: ${({ theme }): string => theme.colors.whiteSmoke}; 
  border-radius: 4px;
`

const ModalClose = styled.span`
  position: absolute;
  top: -1rem;
  right: -1rem;
  display: inline-block;
  vertical-align: top;
  cursor: pointer;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  background-color: ${({ theme }): string => theme.colors.charcoal};
  transition: background-color .3s ease;

  &:before,
  &:after {
    content: "";
    width: 10px;
    height: 2px;
    background-color: ${({ theme }): string => theme.colors.white};
    position: absolute;
    top: 9px;
    left: 5px;
  }

  &:before {
    transform: rotate(-45deg);
  }

  &:after {
    transform: rotate(45deg);
  }

  &:hover {
    background-color: ${({ theme }): string => theme.colors.grey};
  }
`

const ModalHeaderWrapper = styled.div<{ variant: Variant }>`
  display: flex;
  color: ${({ theme }): string => theme.colors.white}; 
  border-radius: 3px 3px 0 0;
  overflow: hidden;
  padding: 1.6rem 2rem 1.4rem;
  ${({ theme, variant }) => {
    if (variant === "negative") {
      return css`
        background-color: ${theme.colors.fireBrick};
      `
    }
    return css`
      background-color: ${theme.colors.indigo};
    `
  }}
`

const ModalHeader = styled.h4`
  font-weight: 600;
  margin-bottom: 0;
`

export const ModalContent = styled.div`
  padding: 2rem 2rem 1.8rem;
  max-height: calc(100vh - 120px);
  max-height: calc(100dvh - 120px);
  overflow-y: auto;

  @media (min-width: ${({ theme }): string => theme.screen.xs}) {
    max-height: calc(100vh - 126px);
    max-height: calc(100dvh - 126px);
  }
`

export const ModalContentResetTop = styled(ModalContent)`
  padding-top: 0;
`

export const DangerLabel = styled.label`
  display: block;
  margin-top: 5px;
  border-color: ${({ theme }): string => theme.colors.lightCoral};
  color: ${({ theme }): string => theme.colors.lightCoral};
  font-weight: 600;
`

export const ModalLabel = styled.label`
  font-weight: 600;
  position: relative;
  display: block;
  color: ${({ theme }): string => theme.colors.charcoal};
  letter-spacing: .05em;
  text-transform: uppercase;
  margin-bottom: 0.8rem;
  margin-top: 1.3rem;
`

export const ModalInputText = styled.input<{ isError?: boolean }>`
  border: 1px solid ${({ theme, isError }): string => isError ? theme.colors.lightCoral : theme.colors.gainsboro};
  border-radius: 3px;
  padding: .8rem 1rem .8rem 1rem;
  height: 40px;
  line-height: normal;
  resize: none;
  width: 100%;
  color: ${({ theme }): string => theme.colors.dimGray};
  
  &:focus {
    outline: none;
  }
`

export const ModalTextArea = styled.textarea`
  border: 1px solid ${({ theme }): string => theme.colors.gainsboro};
  border-radius: 3px;
  padding: .8rem 1rem .8rem 1rem;
  line-height: normal;
  resize: none;
  width: 100%;
  color: ${({ theme }): string => theme.colors.dimGray};

  &:focus {
    outline: none;
  }
`

export const ModalCheckBox = styled.input`
  display: inline-block;
  vertical-align: middle;
  position: relative;
  bottom: 2px;
  margin-right: 1rem;
`

export const ModalCheckBoxLabel = styled.label`
  font-weight: 600;
`

export const ModalSubmitWrapper = styled.div`
  position: relative;
  border-radius: 0 0 4px 4px;
`

export const ModalSubmitButton = styled.button`
  display: block;
  width: 100%;
  height: 54px;
  font-weight: 600;
  cursor: pointer;
  text-align: center;
  padding: 0 1rem;
  letter-spacing: .1em;
  line-height: 54px;
  text-transform: uppercase;
  color: ${({ theme }): string => theme.colors.white};
  background-color: ${({ theme }): string => theme.colors.tropicalRainForest};
  border-radius: 0 0 4px 4px;
  transition: background-color .3s ease;
  
  &:hover {
    background-color: ${({ theme }): string => darken(0.05, theme.colors.tropicalRainForest)};
  }
`

export const DropdownWrapper = styled.div`
  margin-bottom: 1.2rem;
  background-color: ${({ theme }): string => theme.colors.white};
`

const WhiteLoader = styled(Loader)`
  border-top: 3px solid ${({ theme }): string => theme.colors.white};
  margin-left: 12px;
  margin-top: 4px;
`

const LoaderWrapper = styled.div`
  margin-left: auto;
`

const LoaderLabel = styled.span`
  font-size: 1rem;
  line-height: 2.8rem;
  vertical-align: top;
`

export type DialogStatus = "OPEN" | "CLOSED" | "ERROR"

type Variant = "regular" | "negative"

type Props = {
  title?: string,
  className?: string,
  variant?: Variant,
  children?: React.ReactChild | React.ReactChild[],
  loadingMessage?: string,
  closeOnEscape?: boolean,
  onClose: () => void,
}

const Dialog = ({
  title = "",
  className,
  variant = "regular",
  children,
  loadingMessage,
  closeOnEscape = true,
  onClose,
}: Props): React.ReactElement<Props> => {
  const dialogRef = useRef(null)
  useClickAway(dialogRef, (event: Event) => {
    // eslint-disable-next-line lingui/no-unlocalized-strings
    const isNotToast = !(event?.target as HTMLElement)?.classList?.contains("Toastify__toast-body")
    if (isNotToast) {
      onClose()
    }
  })

  useKey("Escape", () => {
    if (closeOnEscape) {
      onClose()
    }
  }, undefined, [closeOnEscape])

  return ReactDOM.createPortal(
    <DialogWrapper
      className={DIALOG_WRAPPER_CLASSNAME}
      onClick={(event): void => {
        event.stopPropagation()
      }}
    >
      <Modal ref={dialogRef} className={className}>
        <ModalClose onClick={onClose}/>
        {title !== "" && (
          <ModalHeaderWrapper variant={variant}>
            <ModalHeader>{title}</ModalHeader>
            {loadingMessage && (
              <LoaderWrapper>
                <LoaderLabel>
                  {loadingMessage}
                </LoaderLabel>
                <WhiteLoader/>
              </LoaderWrapper>)
            }
          </ModalHeaderWrapper>
        )}
        {children}
      </Modal>
    </DialogWrapper>,
    document.getElementById("root") as HTMLElement,
  )
}

export default Dialog
